/**
 * Step 4 -- COSMA sales rates ×3 (f10.7), rendered inside f10.3's step frame
 * through `STEP_BODIES`. Follows `cycle-step.html`, once per scope tab:
 *
 *   upload -> process -> what did not match -> decide -> resulting rates -> accept
 *
 * The tabs are the server's scope list (a lookup), each with its committed
 * badge and currency. A tab shows its newest batch that has not been
 * discarded. Upload and commit each answer `202` with a job watched through
 * the step's job route.
 */

import { useEffect, useRef, useState } from 'react';
import { Check, CheckCircle2, X } from 'lucide-react';

import type { CycleWorkflowStep, SalesRateReview, SalesRateScope } from '@/api/client';
import {
  isJobTerminal,
  useCommitSalesRateBatch,
  useDiscardSalesRateBatch,
  useRefreshSalesRates,
  useSalesRateBatches,
  useSalesRateJob,
  useSalesRateReview,
  useSalesRateScopes,
  useUploadSalesRateBatch,
} from '@/api/queries';
import { JobProgress } from '@/jobs/JobProgress';

import { SalesRatesCommitDialog } from './SalesRatesCommitDialog';
import {
  SalesRatesDropzone,
  SalesRatesFileCard,
  SalesRatesProcessingCard,
  SalesRatesRatesCard,
} from './SalesRatesCards';
import { UnmatchedRatesCard } from './UnmatchedRatesCard';
import { hasStagedItems, isParsing, refusalMessage } from './salesRatesView';

interface WatchedJob {
  id: string;
  kind: 'parse' | 'commit';
}

export function SalesRatesStep({ cycleId }: { cycleId: string; step: CycleWorkflowStep }) {
  const scopes = useSalesRateScopes(cycleId);
  const [active, setActive] = useState<string | null>(null);

  if (scopes.isPending) return <p className="hint">Loading step 4…</p>;
  if (scopes.isError) {
    return <ErrorAlert message={refusalMessage(scopes.error) ?? 'Step 4 could not be loaded.'} />;
  }
  const list = scopes.data.scopes;
  const current = list.find((scope) => scope.code === active) ?? list[0];
  if (!current) return <p className="hint">No COSMA sales-rate scope is configured.</p>;

  return (
    <div className="stack">
      <div className="tabs" role="tablist" aria-label="COSMA sales-rate scopes">
        {list.map((scope) => (
          <button
            key={scope.code}
            type="button"
            role="tab"
            aria-selected={scope.code === current.code}
            className={`tab${scope.code === current.code ? ' is-active' : ''}`}
            onClick={() => setActive(scope.code)}
          >
            {scope.current_batch_id ? <CheckCircle2 aria-label="Committed" className="us-eng-ok" /> : null}
            {scope.name}
            <span className="count">
              {scope.currency_code} &middot; {scope.current_batch_id ? 'Committed' : 'Pending'}
            </span>
          </button>
        ))}
      </div>
      <ScopePanel key={current.code} cycleId={cycleId} scope={current} />
    </div>
  );
}

function ScopePanel({ cycleId, scope }: { cycleId: string; scope: SalesRateScope }) {
  const batches = useSalesRateBatches(cycleId);
  const upload = useUploadSalesRateBatch(cycleId);
  const [watched, setWatched] = useState<WatchedJob | null>(null);

  const shown = batches.data?.batches.find(
    (batch) => batch.scope?.code === scope.code && batch.state.code !== 'DISCARDED',
  );
  const parsing = shown ? isParsing(shown.state.code) : false;
  const review = useSalesRateReview(cycleId, shown && !parsing ? shown.id : undefined);
  const job = useSalesRateJob(cycleId, watched?.id);
  useJobCompletion(cycleId, watched, job.data?.state);

  const startUpload = (file: File) =>
    upload.mutate(
      { file, scope: scope.code },
      { onSuccess: (accepted) => setWatched({ id: accepted.job_id, kind: 'parse' }) },
    );

  if (batches.isPending) return <p className="hint">Loading {scope.name}…</p>;
  if (batches.isError) return <ErrorAlert message={refusalMessage(batches.error)} />;

  const jobCard = job.data && (!isJobTerminal(job.data.state) || job.data.state === 'FAILED') && (
    <div className="card">
      <div className="card-body">
        <JobProgress
          job={job.data}
          title={watched?.kind === 'commit' ? 'Writing sales rates' : `Reading the ${scope.name} report`}
        />
      </div>
    </div>
  );

  return (
    <div className="stack">
      {upload.error ? <ErrorAlert message={refusalMessage(upload.error)} /> : null}
      {jobCard}

      {!shown && (
        <SalesRatesDropzone
          scopeName={scope.name}
          currency={scope.currency_code}
          onFile={startUpload}
          busy={upload.isPending}
        />
      )}

      {shown && parsing && !jobCard && (
        <p className="hint" role="status">
          Reading {shown.original_filename ?? 'the uploaded file'}…
        </p>
      )}

      {review.isError && <ErrorAlert message={refusalMessage(review.error)} />}

      {review.data && (
        <>
          <SalesRatesFileCard review={review.data} onReplace={startUpload} busy={upload.isPending} />
          <SalesRatesProcessingCard review={review.data} />
          {hasStagedItems(review.data.batch.state.code) && (
            <BatchDecisions
              cycleId={cycleId}
              review={review.data}
              onCommitStarted={(jobId) => setWatched({ id: jobId, kind: 'commit' })}
            />
          )}
          {review.data.batch.state.code === 'COMMITTED' && (
            <SalesRatesRatesCard figures={review.data.figures} currency={scope.currency_code} />
          )}
        </>
      )}
    </div>
  );
}

/** Refetch once a watched job reaches a terminal state -- once per job. */
function useJobCompletion(cycleId: string, watched: WatchedJob | null, state: string | undefined) {
  const refresh = useRefreshSalesRates(cycleId);
  const handled = useRef<string | null>(null);

  useEffect(() => {
    if (!watched || !isJobTerminal(state) || handled.current === watched.id) return;
    handled.current = watched.id;
    refresh(watched.kind === 'commit' && state === 'SUCCEEDED');
  }, [watched, state, refresh]);
}

function BatchDecisions({
  cycleId,
  review,
  onCommitStarted,
}: {
  cycleId: string;
  review: SalesRateReview;
  onCommitStarted: (jobId: string) => void;
}) {
  const commit = useCommitSalesRateBatch(cycleId, review.batch.id);
  const discard = useDiscardSalesRateBatch(cycleId, review.batch.id);
  const [confirming, setConfirming] = useState(false);
  const { capabilities } = review;

  const footer = (capabilities.can_commit || capabilities.can_discard) && (
    <div className="card-foot">
      <span className="hint">
        {capabilities.can_commit
          ? 'Accepting stores twelve monthly rates and the annual rate for every kept key.'
          : 'Keep or exclude every rate key above to enable Accept and commit.'}
      </span>
      <div className="btn-row">
        {capabilities.can_discard && (
          <button
            type="button"
            className="btn btn-secondary"
            disabled={discard.isPending}
            onClick={() => discard.mutate()}
          >
            <X aria-hidden="true" /> Discard batch
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary"
          disabled={!capabilities.can_commit}
          onClick={() => setConfirming(true)}
        >
          <Check aria-hidden="true" /> Accept and commit
        </button>
      </div>
    </div>
  );

  return (
    <>
      <UnmatchedRatesCard cycleId={cycleId} review={review} />
      {discard.error ? <ErrorAlert message={refusalMessage(discard.error)} /> : null}
      <SalesRatesRatesCard
        figures={review.figures}
        currency={review.batch.scope?.currency_code ?? ''}
        footer={footer || undefined}
      />
      {confirming && (
        <SalesRatesCommitDialog
          review={review}
          busy={commit.isPending}
          error={commit.error}
          onClose={() => {
            setConfirming(false);
            commit.reset();
          }}
          onConfirm={() =>
            commit.mutate(undefined, {
              onSuccess: (accepted) => {
                setConfirming(false);
                onCommitStarted(accepted.job_id);
              },
            })
          }
        />
      )}
    </>
  );
}

function ErrorAlert({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="alert alert-danger" role="alert">
      <div>
        <div className="a-body">{message}</div>
      </div>
    </div>
  );
}
